import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Button, Dropdown, DropdownItem, DropdownSeparator, Spinner, useToast } from '../ui';
import { useAccountRefresh } from '../features/clients';
import { useClientDeletion } from '../features/clients/useClientDeletion';
import { canDeleteClientData } from '../../utils/permissions';
import { useUser } from '../../contexts/UserContext';

/**
 * Per-client overflow menu.
 *
 * Previously near-useless: one item ("View Details") that duplicated clicking
 * the row, plus a "Refresh Account Data" item gated on `platform === 'X'` - so
 * a TikTok-only client got a single-item menu that did nothing new. It also
 * reported success to console.log, meaning the refresh looked like it did
 * nothing at all.
 *
 * Now it carries the actions that are real but not daily: adding an account
 * (moved out of the row, where it occupied the primary slot) and refreshing
 * profile data for every platform whose adapter supports it - which is all of
 * them since account enrichment stopped being X-only.
 */
export const ClientActionsDropdown = ({ client, onAddAccount, className, showView = true, onDeletionAccepted }) => {
	const navigate = useNavigate();
	const toast = useToast();
	const { user } = useUser();
	const { beginDeletion, pendingId } = useClientDeletion();
	const { refreshAllClientAccounts, isRefreshing } = useAccountRefresh();

	const accounts = client.accounts || [];
	const hasAccounts = accounts.length > 0;
	const isRefreshingAny = accounts.some((account) => isRefreshing(account._id));

	const handleRefreshAccounts = async () => {
		try {
			const result = await refreshAllClientAccounts(client._id, accounts);
			const n = result?.successCount ?? accounts.length;
			toast.success(`Refreshed ${n} account${n === 1 ? '' : 's'}`, {
				description: `Profile data updated for ${client.name}.`,
			});
		} catch (error) {
			toast.error('Could not refresh accounts', { description: error.message });
		}
	};

	return (
		<Dropdown
			trigger={
				<Button variant="outline" size="sm" aria-label={`Actions for ${client.name}`}>
					<DotsHorizontalIcon className="h-4 w-4" />
				</Button>
			}
			align="right"
			className={className}
		>
			{showView && <DropdownItem onClick={() => navigate(`/clients/${client._id}`)}>View client</DropdownItem>}
			<DropdownItem onClick={() => onAddAccount?.(client._id, client.name)}>
				Add account
			</DropdownItem>

			{hasAccounts && (
				<>
					<DropdownSeparator />
					<DropdownItem onClick={handleRefreshAccounts} disabled={isRefreshingAny}>
						{isRefreshingAny ? (
							<span className="flex items-center gap-2">
								<Spinner size="sm" />
								Refreshing…
							</span>
						) : (
							'Refresh profile data'
						)}
					</DropdownItem>
				</>
			)}
			{canDeleteClientData(user) && (
				<>
					<DropdownSeparator />
					<DropdownItem className="text-destructive" disabled={pendingId === client._id} onClick={() => beginDeletion('client', client, onDeletionAccepted)}>
						{pendingId === client._id ? 'Checking deletion...' : 'Delete client'}
					</DropdownItem>
				</>
			)}
		</Dropdown>
	);
};

export default ClientActionsDropdown;
